import React, { useEffect } from 'react';
import {
    Box,
    Drawer,
    Button,
    Stack,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    DialogTitle,
} from '@mui/material';
import { useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import { subHours, subDays, startOfDay } from 'date-fns';

function sincePresetToIso(preset) {
    const now = new Date();
    switch (preset) {
        case '24h':
            return subHours(now, 24).toISOString();
        case '7d':
            return subDays(now, 7).toISOString();
        case 'today':
            return startOfDay(now).toISOString();
        default:
            return '';
    }
}

function isoToPreset(iso) {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const now = Date.now();
    const todayStart = startOfDay(new Date()).getTime();
    if (Math.abs(t - todayStart) < 60_000) return 'today';
    if (Math.abs(t - subHours(new Date(), 24).getTime()) < 5 * 60_000) return '24h';
    if (Math.abs(t - subDays(new Date(), 7).getTime()) < 5 * 60_000) return '7d';
    return 'custom';
}

export function SearchDrawer({ open, onClose, onClearFilters }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const { values, handleSubmit, handleChange, resetForm, setFieldValue } = useFormik({
        initialValues: {
            search:
                searchParams.get('name') ||
                searchParams.get('search') ||
                searchParams.get('businessName') ||
                searchParams.get('document') ||
                searchParams.get('state') ||
                searchParams.get('city') ||
                '',
            status: searchParams.get('status') || '',
            approved: searchParams.get('approved') || '',
            responsibleName: searchParams.get('responsibleName') || '',
            responsibleEmail: searchParams.get('responsibleEmail') || '',
            broker: searchParams.get('broker') || '',
            orderBy: searchParams.get('orderBy') || 'updatedAt_desc',
            updatedSincePreset: isoToPreset(searchParams.get('updatedSince')) || '',
            offerActivity: searchParams.get('hasInactiveOffer') === 'true'
                ? 'inactive'
                : searchParams.get('hasActiveOffer') === 'true'
                    ? 'active'
                    : '',
            missingCategory: searchParams.get('missingCategory') === 'true' ? 'true' : '',
            contractStatus: searchParams.get('contractStatus') || '',
        },
        onSubmit: formValues => {
            const next = {};
            if (formValues.search) next.name = formValues.search;
            if (formValues.status) next.status = formValues.status;
            if (formValues.approved) next.approved = formValues.approved;
            if (formValues.contractStatus) next.contractStatus = formValues.contractStatus;
            if (formValues.missingCategory === 'true') next.missingCategory = 'true';
            if (formValues.responsibleName) next.responsibleName = formValues.responsibleName;
            if (formValues.responsibleEmail) next.responsibleEmail = formValues.responsibleEmail;
            if (formValues.broker) next.broker = formValues.broker;
            if (formValues.orderBy) next.orderBy = formValues.orderBy;
            if (formValues.offerActivity === 'active') next.hasActiveOffer = 'true';
            if (formValues.offerActivity === 'inactive') next.hasInactiveOffer = 'true';

            const sinceIso = sincePresetToIso(formValues.updatedSincePreset);
            if (sinceIso) next.updatedSince = sinceIso;

            setSearchParams(next);
            onClose();
        },
    });

    useEffect(() => {
        resetForm({
            values: {
                search:
                    searchParams.get('name') ||
                    searchParams.get('search') ||
                    searchParams.get('businessName') ||
                    searchParams.get('document') ||
                    searchParams.get('state') ||
                    searchParams.get('city') ||
                    '',
                status: searchParams.get('status') || '',
                approved: searchParams.get('approved') || '',
                responsibleName: searchParams.get('responsibleName') || '',
                responsibleEmail: searchParams.get('responsibleEmail') || '',
                broker: searchParams.get('broker') || '',
                orderBy: searchParams.get('orderBy') || 'updatedAt_desc',
                updatedSincePreset: isoToPreset(searchParams.get('updatedSince')) || '',
                offerActivity: searchParams.get('hasInactiveOffer') === 'true'
                    ? 'inactive'
                    : searchParams.get('hasActiveOffer') === 'true'
                        ? 'active'
                        : '',
                missingCategory: searchParams.get('missingCategory') === 'true' ? 'true' : '',
                contractStatus: searchParams.get('contractStatus') || '',
            },
        });
    }, [searchParams, resetForm]);

    const handleClearFilters = () => {
        onClearFilters();
        setSearchParams({});
        resetForm();
    };

    const hasFilters = Object.entries(values).some(([key, value]) => {
        if (key === 'orderBy' && value === 'updatedAt_desc') return false;
        return value !== '' && value !== null;
    });

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ padding: 2, width: 350 }}>
                <DialogTitle sx={{ padding: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        Buscar Anunciantes
                        <IconButton size="small" onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Buscar"
                            placeholder="Nome, razão social, documento, estado ou cidade"
                            variant="outlined"
                            fullWidth
                            name="search"
                            value={values.search}
                            onChange={handleChange}
                        />
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="orderBy-label">Ordenar por</InputLabel>
                            <Select
                                labelId="orderBy-label"
                                id="orderBy"
                                name="orderBy"
                                value={values.orderBy}
                                onChange={handleChange}
                                label="Ordenar por"
                            >
                                <MenuItem value="updatedAt_desc">Atualização (mais recentes)</MenuItem>
                                <MenuItem value="createdAt_desc">Criação (mais recentes)</MenuItem>
                                <MenuItem value="id_desc">ID (mais novos)</MenuItem>
                                <MenuItem value="name_asc">Nome (A–Z)</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="updatedSince-label">Atualizado desde</InputLabel>
                            <Select
                                labelId="updatedSince-label"
                                id="updatedSincePreset"
                                name="updatedSincePreset"
                                value={values.updatedSincePreset}
                                onChange={e => setFieldValue('updatedSincePreset', e.target.value)}
                                label="Atualizado desde"
                            >
                                <MenuItem value="">Qualquer data</MenuItem>
                                <MenuItem value="today">Hoje</MenuItem>
                                <MenuItem value="24h">Últimas 24h</MenuItem>
                                <MenuItem value="7d">Últimos 7 dias</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="broker-label">Broker</InputLabel>
                            <Select
                                labelId="broker-label"
                                id="broker"
                                name="broker"
                                value={values.broker}
                                onChange={handleChange}
                                label="Broker"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="awin">Awin</MenuItem>
                                <MenuItem value="lomadee">Lomadee</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="offerActivity-label">Ofertas</InputLabel>
                            <Select
                                labelId="offerActivity-label"
                                id="offerActivity"
                                name="offerActivity"
                                value={values.offerActivity}
                                onChange={handleChange}
                                label="Ofertas"
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="active">Com ofertas ativas</MenuItem>
                                <MenuItem value="inactive">Com ofertas inativas</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                id="status"
                                name="status"
                                value={values.status}
                                onChange={handleChange}
                                label="Status"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Ativo</MenuItem>
                                <MenuItem value="false">Inativo</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="category-filter-label">Categoria</InputLabel>
                            <Select
                                labelId="category-filter-label"
                                id="missingCategory"
                                name="missingCategory"
                                value={values.missingCategory}
                                onChange={handleChange}
                                label="Categoria"
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="true">Sem categoria</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="approved-label">Aprovação</InputLabel>
                            <Select
                                labelId="approved-label"
                                id="approved"
                                name="approved"
                                value={values.approved}
                                onChange={handleChange}
                                label="Aprovação"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Aprovados</MenuItem>
                                <MenuItem value="false">Pendentes</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="contractStatus-label">Contrato</InputLabel>
                            <Select
                                labelId="contractStatus-label"
                                id="contractStatus"
                                name="contractStatus"
                                value={values.contractStatus}
                                onChange={handleChange}
                                label="Contrato"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="any">Com contrato (qualquer etapa)</MenuItem>
                                <MenuItem value="none">Sem contrato</MenuItem>
                                <MenuItem value="not_sent">Só prévia (não gerou o PDF)</MenuItem>
                                <MenuItem value="generated">Contrato gerado (assinado ou não)</MenuItem>
                                <MenuItem value="pending">Gerado, aguardando assinatura</MenuItem>
                                <MenuItem value="signed">Assinado</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Nome do Responsável"
                            variant="outlined"
                            fullWidth
                            name="responsibleName"
                            value={values.responsibleName}
                            onChange={handleChange}
                        />
                        <TextField
                            label="E-mail do Responsável"
                            variant="outlined"
                            fullWidth
                            name="responsibleEmail"
                            value={values.responsibleEmail}
                            onChange={handleChange}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button variant="contained" type="submit">
                            Buscar
                        </Button>
                        {hasFilters && (
                            <Button variant="outlined" onClick={handleClearFilters}>
                                Limpar Filtros
                            </Button>
                        )}
                    </Stack>
                </form>
            </Box>
        </Drawer>
    );
}
